/**
 * ⟦C57 · lot 5d · A-174⟧ **Les textes COURTS de la Bibliothèque et de la fiche d'une vidéo.**
 *
 * Matt, 02/10/2026 : *« tout doit être simplifié : ne mettre que les mots ou petites phrases
 * courtes importantes. »* À l'écran, la Bibliothèque ne dit plus que des chiffres et des phrases de
 * 12 mots au plus ; les explications d'avant (pourquoi la rente, ce que coûte une nulle de trop, ce
 * que fait une série, ce que le moteur ne branche pas encore) vivent dans « Les dessous »
 * (`DessousBibliotheque.tsx`, et la fiche pour ce qui est de la vidéo ouverte). Rien n'est
 * supprimé : le texte long est conservé tel quel, de l'autre côté du bouton.
 *
 * Ce module ne fait que composer des CHAÎNES, sans DOM ni store : `textes-courts.test.ts` les
 * mesure avec le même mètre que l'écran (`game/dessous.ts`), pour que la règle tienne dans les
 * tests et plus seulement à l'œil.
 */

import { nombre } from "../../game/format";

/** « 2 % » — un pourcentage entier ou à `decimales` décimales, à la française. */
export function pourcentage(v: number, decimales = 0): string {
  return `${(v * 100).toFixed(decimales).replace(".", ",")} %`;
}

/** « ×0,94 » — le multiplicateur de conversion, tel que le moteur l'appliquera. */
export function multiplicateur(v: number): string {
  return `×${v.toFixed(2).replace(".", ",")}`;
}

/** « ×1,2 » — le multiplicateur de rente d'une série (une décimale). */
export function multiplicateurSerie(v: number): string {
  return `×${v.toFixed(1).replace(".", ",")}`;
}

/** La rente d'un coup d'œil : le nombre d'evergreens, puis les trois chiffres de la semaine. */
export function ligneRenteCourte(o: {
  nb: number;
  vues: number;
  abonnes: number;
  argent: number;
  monetise: boolean;
}): string {
  if (o.nb === 0) return "🌲 Aucune evergreen pour l'instant.";
  return (
    `🌲 ${o.nb} evergreen${o.nb > 1 ? "s" : ""} · ${nombre(o.vues)} vues · ` +
    `${nombre(o.abonnes)} abonné${o.abonnes >= 2 ? "s" : ""}` +
    `${o.monetise ? ` · ${nombre(o.argent)} ₵` : ""} / sem.`
  );
}

/** Les nulles visibles : le nombre, ce qui est de trop, la conversion. Le « pourquoi » est dessous. */
export function ligneNullesCourte(o: {
  visibles: number;
  enTrop: number;
  conversion: number;
}): string {
  const base = `💀 ${o.visibles} nulle${o.visibles > 1 ? "s" : ""} visible${o.visibles > 1 ? "s" : ""}`;
  return o.enTrop > 0
    ? `${base}, ${o.enTrop} de trop · conversion ${multiplicateur(o.conversion)}`
    : `${base} · conversion intacte`;
}

/** Sous la courbe, quand elle n'a pas encore deux points à relier. */
export function attenteCourbeCourte(o: {
  tracees: number;
  joursRestants: number;
  premierJour: number;
}): string {
  if (o.tracees === 1) return `Premier jour encaissé, au J${o.premierJour}.`;
  if (o.joursRestants > 0) return `Rien n'est tombé : premières vues au J${o.premierJour}.`;
  return "Cette vidéo n'a rien rapporté.";
}

/** La note de la courbe : les jours qu'il reste à tomber, en jours absolus (A-8). */
export function noteJoursCourte(o: {
  tout: boolean;
  joursRestants: number;
  premierJour: number;
  dernierJour: number;
}): string {
  const jours = `${o.joursRestants} jour${o.joursRestants > 1 ? "s" : ""}`;
  return o.tout
    ? `Du J${o.premierJour} au J${o.dernierJour} · ${jours} à tomber.`
    : `⏳ ${jours} à tomber, jusqu'au J${o.dernierJour}.`;
}

/** La rente d'UNE vidéo : trois chiffres. Le versement « à chaque nuit » est dans les dessous. */
export function ligneRenteVideoCourte(o: {
  vues: number;
  abonnes: number;
  argent: number;
  monetise: boolean;
}): string {
  return (
    `${nombre(o.vues)} vues · ${nombre(o.abonnes)} abonné${o.abonnes >= 2 ? "s" : ""}` +
    `${o.monetise ? ` · ${nombre(o.argent)} ₵` : ""}`
  );
}

/** Le rang et la force restante d'une evergreen. */
export function ligneRangCourte(o: { rang: number; force: number; retenue: number | null }): string {
  return (
    `Rang ${o.rang} · ${pourcentage(o.force, 0)} de sa force` +
    (o.retenue !== null ? ` · retenue à ${pourcentage(o.retenue, 0)}` : "")
  );
}

/** Sous le bouton « Faire le ménage ». */
export const NOTE_NULLE_COURTE = "Une nulle pèse sur toute la chaîne.";

/** À la place du bouton, tant que le nœud de l'arbre n'est pas pris. */
export const NOTE_MENAGE_FERME_COURTE = "🔒 Le nœud Gestion d'image débloque le ménage.";

/** À la place des séries, tant que le nœud n'est pas pris. */
export const NOTE_SERIES_FERMEES_COURTE = "🔒 Le nœud Lore de chaîne débloque les séries.";

/** Quand aucune série n'existe encore. */
export const AUCUNE_SERIE_COURTE = "Aucune série.";

/** La ligne de pied de la Bibliothèque : le clin d'œil de la maison, sans son préambule. */
export const CLIN_D_OEIL_COURT = "Le vrai pro n'a laissé que 21 longues visibles.";

/** Une série de la liste : son nom, ses vidéos, sa rente croisée. */
export function ligneSerieCourte(o: { nom: string; videos: number; mult: number }): string {
  return `« ${o.nom} » · ${o.videos} vidéos · rente ${multiplicateurSerie(o.mult)}`;
}

/** Le dialogue « Passer cette vidéo en privé ? » : le nom, l'effet en chiffres, le coût. */
export function corpsMenage(o: {
  nomVideo: string;
  avant: number;
  apres: number;
  cout: string;
}): string {
  return (
    `« ${o.nomVideo} »\n` +
    `Privée : plus rien n'est versé, conversion ${multiplicateur(o.avant)} → ${multiplicateur(o.apres)}.\n` +
    `Coût : ${o.cout}.`
  );
}

/** Le dialogue « Remasteriser cette evergreen ? » : l'effet promis, le coût, et l'aveu du stub. */
export function corpsRemaster(o: { nomVideo: string; cout: string }): string {
  return (
    `« ${o.nomVideo} »\n` +
    `Nouveau tour de rente, coût : ${o.cout}.\n` +
    `⚙ Pas encore branché : tu paierais pour rien.`
  );
}

/** Le dialogue « Ranger en série » : combien, l'effet promis, et l'aveu du stub. */
export function corpsSerie(o: { combien: number; mult: number }): string {
  return (
    `${o.combien} vidéos forment une série : rente ${multiplicateurSerie(o.mult)}.\n` +
    `⚙ Pas encore branché : le moteur facture sans créer la série.`
  );
}
